import { CircleCheckBig, CircleX, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDisplayDateTime } from '@/utils/date';

export type ReviewedStatus = 'APPROVED' | 'REJECTED';

interface ReviewOutcomeDisplay {
  verb: string;
  icon: LucideIcon;
  surfaceClass: string;
  accentClass: string;
}

const REVIEW_OUTCOME_DISPLAY: Record<ReviewedStatus, ReviewOutcomeDisplay> = {
  APPROVED: {
    verb: 'approved',
    icon: CircleCheckBig,
    surfaceClass: 'border-success-foreground/30 bg-success/60',
    accentClass: 'text-success-foreground',
  },
  REJECTED: {
    verb: 'rejected',
    icon: CircleX,
    surfaceClass: 'border-danger-foreground/30 bg-danger/60',
    accentClass: 'text-danger-foreground',
  },
};

interface ReviewOutcomeSummaryProps {
  subject: string;
  status: ReviewedStatus;
  reviewedByName: string | null;
  reviewedAt: string | null;
  reviewerComment: string | null;
}

export function ReviewOutcomeSummary({
  subject,
  status,
  reviewedByName,
  reviewedAt,
  reviewerComment,
}: ReviewOutcomeSummaryProps) {
  const { verb, icon: OutcomeIcon, surfaceClass, accentClass } = REVIEW_OUTCOME_DISPLAY[status];

  return (
    <section
      aria-label="Review result"
      className={cn('flex gap-3 rounded-xl border p-4', surfaceClass)}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-full bg-card shadow-xs',
          accentClass,
        )}
      >
        <OutcomeIcon className="size-5" aria-hidden />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <p className={cn('text-base font-semibold', accentClass)}>
            This {subject} was {verb}
          </p>
          <p className="text-sm text-muted-foreground">
            {reviewedByName ? (
              <>
                By <span className="font-medium text-foreground">{reviewedByName}</span>
              </>
            ) : (
              'Reviewer not recorded'
            )}
            {reviewedAt && (
              <>
                <span className="max-sm:hidden"> · </span>
                <time dateTime={reviewedAt} className="whitespace-nowrap max-sm:block">
                  {formatDisplayDateTime(reviewedAt)}
                </time>
              </>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Reviewer comment</span>
          <p className="rounded-lg bg-card px-3 py-2 text-sm leading-relaxed wrap-break-word whitespace-pre-line">
            {reviewerComment ?? <span className="text-muted-foreground">No comment was left.</span>}
          </p>
        </div>
      </div>
    </section>
  );
}
